/*
 * Two small hooks instead of a data-fetching library.
 *
 * The whole application has about a dozen distinct reads and no shared cache
 * requirement worth a dependency: the admin interface must always show current
 * data (a cached article list is actively harmful when someone else just published
 * something), and the public site is cached by HTTP where it matters.
 *
 * What is worth getting right, and is easy to get wrong by hand, is the two ways an
 * async effect corrupts state: a slow response arriving after the component has
 * unmounted, and an earlier response arriving after a later one. Both are handled
 * here once.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Normalises a caught value to an Error.
 *
 * Both hooks expose `Error | null` rather than `unknown`, which matters in JSX:
 * `{state.error && <ErrorBanner …/>}` with an `unknown` error types the whole
 * expression as `unknown`, which is not a ReactNode. Narrowing here once removes a
 * ternary from every call site, and instanceof ApiError still works because ApiError
 * extends Error.
 */
function toError(caught: unknown): Error {
  if (caught instanceof Error) return caught;
  return new Error(typeof caught === 'string' ? caught : 'Something went wrong.');
}

export interface AsyncState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  /** Re-runs the loader. */
  reload: () => void;
  /** Replaces the data without a round trip, for responses that return the new state. */
  setData: (next: T) => void;
}

/**
 * Runs `loader` on mount and whenever `deps` change.
 *
 * The abort signal is passed through so an in-flight request for a stale filter is
 * cancelled rather than raced, and a generation counter guards against a response
 * that arrives anyway.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  const generation = useRef(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    const current = ++generation.current;
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    loaderRef.current(controller.signal)
      .then((result) => {
        if (generation.current !== current) return;
        setData(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (generation.current !== current) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(toError(caught));
      })
      .finally(() => {
        if (generation.current === current) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  return { data, error, loading, reload, setData };
}

export interface ActionState {
  running: boolean;
  error: Error | null;
  /** Runs `work`, tracking progress and capturing the failure. */
  run: <T>(work: () => Promise<T>) => Promise<T | undefined>;
  clearError: () => void;
}

/**
 * For writes. Returns undefined rather than throwing when the work fails, so a
 * click handler does not need its own try/catch, and exposes the error for the UI
 * to render.
 */
export function useAction(): ActionState {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async <T,>(work: () => Promise<T>): Promise<T | undefined> => {
    setRunning(true);
    setError(null);
    try {
      const result = await work();
      return result;
    } catch (caught) {
      if (mounted.current) setError(toError(caught));
      return undefined;
    } finally {
      if (mounted.current) setRunning(false);
    }
  }, []);

  return { running, error, run, clearError: useCallback(() => setError(null), []) };
}

/** Delays a rapidly-changing value, for search boxes. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
